/**
 * Live ad-pack Firestore listener.
 *
 * Subscribes to the per-ad state machine the agent service writes into
 * the ``adpacks_live`` collection (separate from the canonical
 * ``adpacks`` GCS bundle). This is the streaming counterpart to the
 * post-completion Canvas API — the UI hydrates ad cards as briefs /
 * images / reviews complete, instead of waiting for the whole pack.
 *
 * Schema (mirrors app/ad_generation/live/models.py):
 *
 *   adpacks_live/{job_id}                         (root doc — LiveAdPack)
 *   adpacks_live/{job_id}/ads/{ad_id}             (LiveAd subdocs)
 *   adpacks_live/{job_id}/events/{event_id}       (append-only event log)
 */

import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import { getFirestoreDb } from './firebase';
import { getApiBase } from './api';

// ---------------------------------------------------------------------------
// Types — mirror the Pydantic models on the backend.
// ---------------------------------------------------------------------------

export type LiveAdState =
  | 'pending'
  | 'brief_ready'
  | 'image_preview'
  | 'image_ready'
  | 'reviewed'
  | 'final'
  | 'failed';

export type LiveAdPackStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface LiveAdDoc {
  ad_id: string;
  idx: number;
  state: LiveAdState;
  headline?: string | null;
  hook?: string | null;
  body?: string | null;
  cta?: string | null;
  persona_id?: string | null;
  product_id?: string | null;
  visual_style?: string | null;
  template_id?: string | null;
  preview_image_url?: string | null;
  /**
   * Inline base64 JPEG thumbnail (data: URL). Populated at
   * ``image_preview`` so the UI can show actual pixel content while
   * the full-resolution upload is still in flight. Typically 5-8KB.
   */
  preview_data_url?: string | null;
  image_url?: string | null;
  image_width?: number | null;
  image_height?: number | null;
  review_score?: number | null;
  review_summary?: string | null;
  review_pass?: boolean | null;
  asset_url?: string | null;
  canvas_asset_id?: string | null;
  error?: string | null;
  error_phase?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface LiveAdPackDoc {
  job_id: string;
  pack_id?: string | null;
  client_uid: string;
  brief_id?: string | null;
  persona_ids?: string[];
  status: LiveAdPackStatus;
  expected_ads: number;
  completed_ads: number;
  failed_ads: number;
  current_phase?: string | null;
  pipeline_message?: string | null;
  error?: string | null;
  started_at?: string;
  updated_at?: string;
  completed_at?: string | null;
  expires_at?: string | null;
}

export interface LiveAdPackSnapshot {
  /** Root doc, or null until the pipeline has emitted ``init_pack``. */
  pack: LiveAdPackDoc | null;
  /** Per-ad documents indexed by ``ad_id`` (typically a brief_id). */
  ads: Record<string, LiveAdDoc>;
  /** Stable ordering by ``idx`` for placeholder grid layout. */
  adsOrdered: LiveAdDoc[];
}

export type LiveAdPackHandler = (snapshot: LiveAdPackSnapshot) => void;

export interface LiveAdPackErrorInfo {
  /** Human-readable message — typically Firestore permission-denied. */
  message: string;
  /** Original Firebase error code if available (e.g. "permission-denied"). */
  code?: string;
  /** Which subscription emitted the error: pack root doc or ads subcollection. */
  source: 'pack' | 'ads';
}

// ---------------------------------------------------------------------------
// Subscription
// ---------------------------------------------------------------------------

const COLLECTION = 'adpacks_live';
const SUBCOLLECTION_ADS = 'ads';

/**
 * Subscribe to a live ad-pack. Calls ``handler`` on every change to the
 * root doc OR any ad subdoc — handler always receives the full latest
 * snapshot so React renders are simple.
 *
 * ``onError`` (optional) is called whenever the pack or ads listener
 * surfaces a Firestore error — most commonly ``permission-denied`` when
 * security rules block reads. The caller can then decide whether to
 * surface it in the UI and/or fall over to a polling transport.
 *
 * Returns an unsubscribe function. Safe to call repeatedly with the
 * same job_id; each call gets its own Firestore listener.
 */
export function subscribeToLiveAdPack(
  jobId: string,
  handler: LiveAdPackHandler,
  onError?: (info: LiveAdPackErrorInfo) => void,
): Unsubscribe {
  const db = getFirestoreDb();
  const packRef = doc(db, COLLECTION, jobId);
  const adsRef = collection(db, COLLECTION, jobId, SUBCOLLECTION_ADS);
  const adsQuery = query(adsRef, orderBy('idx', 'asc'));

  let pack: LiveAdPackDoc | null = null;
  const adsById: Record<string, LiveAdDoc> = {};

  function emit() {
    const adsOrdered = Object.values(adsById).slice().sort((a, b) => a.idx - b.idx);
    handler({ pack, ads: adsById, adsOrdered });
  }

  function reportError(source: 'pack' | 'ads', err: unknown) {
    console.warn(`[liveAdPack] ${source} listener error`, err);
    if (!onError) return;
    const e = err as { message?: string; code?: string };
    onError({
      message: e?.message ?? String(err),
      code: e?.code,
      source,
    });
  }

  const unsubPack = onSnapshot(
    packRef,
    (snap) => {
      pack = snap.exists() ? (snap.data() as LiveAdPackDoc) : null;
      emit();
    },
    (err) => reportError('pack', err),
  );

  const unsubAds = onSnapshot(
    adsQuery,
    (snap) => {
      snap.docChanges().forEach((change) => {
        const data = change.doc.data() as LiveAdDoc;
        if (change.type === 'removed') {
          delete adsById[data.ad_id];
        } else {
          adsById[data.ad_id] = data;
        }
      });
      emit();
    },
    (err) => reportError('ads', err),
  );

  return () => {
    try {
      unsubPack();
    } catch (e) {
      // ignore
    }
    try {
      unsubAds();
    } catch (e) {
      // ignore
    }
  };
}

// ---------------------------------------------------------------------------
// HTTP polling fallback
//
// When Firestore reads are blocked (security rules, project mismatch,
// missing creds) the agent service exposes a polling endpoint that
// returns the live publisher's local mirror. Same data shape; trades
// real-time push for ~2s latency.
// ---------------------------------------------------------------------------

const AGENT_BASE_URL = getApiBase();

const DEFAULT_POLL_INTERVAL_MS = 2000;

interface LiveStateResponse {
  pack: LiveAdPackDoc | null;
  ads: LiveAdDoc[];
  publisher_attached?: boolean;
}

/**
 * Poll the agent service for live state and call ``handler`` on every
 * tick. Each tick returns the publisher's full local mirror — this is
 * the same data shape the Firestore listener produces, so callers can
 * treat the two transports interchangeably.
 *
 * ``handler`` receives ``null`` on a 404 (job not tracked) or network
 * error so the UI can render an "unavailable" state.
 *
 * Returns an unsubscribe function.
 */
export function pollLiveAdPackState(
  jobId: string,
  handler: (snapshot: LiveAdPackSnapshot | null, publisherAttached: boolean) => void,
  intervalMs: number = DEFAULT_POLL_INTERVAL_MS,
): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  async function tick() {
    if (cancelled) return;
    try {
      const r = await fetch(
        `${AGENT_BASE_URL}/api/v1/ad-generation/jobs/${encodeURIComponent(jobId)}/live-state`,
        { headers: { Accept: 'application/json' } },
      );
      if (cancelled) return;
      if (!r.ok) {
        handler(null, false);
      } else {
        const data = (await r.json()) as LiveStateResponse;
        const adsOrdered = (data.ads ?? []).slice().sort((a, b) => a.idx - b.idx);
        const ads: Record<string, LiveAdDoc> = {};
        for (const ad of adsOrdered) ads[ad.ad_id] = ad;
        handler(
          { pack: data.pack ?? null, ads, adsOrdered },
          !!data.publisher_attached,
        );
      }
    } catch (err) {
      if (cancelled) return;
      console.warn('[liveAdPack] poll error', err);
      handler(null, false);
    } finally {
      if (!cancelled) {
        timer = setTimeout(tick, intervalMs);
      }
    }
  }

  // Fire one tick immediately so the UI doesn't sit empty for the
  // first interval.
  void tick();

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
}

/**
 * Higher-order subscription that runs the Firestore listener AND a
 * polling fallback in parallel, merging their snapshots so whichever
 * transport has data drives the UI. Used by the LiveAssetViewerPage so
 * the streaming view works regardless of Firestore reachability.
 *
 * Strategy:
 *  - Firestore primary; emit on every push update.
 *  - Polling secondary; only "wins" when its data is newer than what
 *    Firestore has shown so far (covers the common case where Firestore
 *    reads are blocked → its snapshot stays empty → polling fills in).
 */
export interface CombinedSnapshot extends LiveAdPackSnapshot {
  source: 'firestore' | 'polling' | 'none';
  /** Set by Firestore listener when the read fails. */
  firestoreError?: LiveAdPackErrorInfo;
  /** True when the agent service confirmed a publisher is attached. */
  publisherAttached?: boolean;
}

// How long to wait for Firestore to deliver data before activating the
// polling fallback. If Firestore is healthy this timer never fires.
const FIRESTORE_GRACE_PERIOD_MS = 5000;

export function subscribeToLiveAdPackCombined(
  jobId: string,
  handler: (snap: CombinedSnapshot) => void,
  options: { pollIntervalMs?: number } = {},
): () => void {
  let firestoreSnap: LiveAdPackSnapshot = { pack: null, ads: {}, adsOrdered: [] };
  let pollingSnap: LiveAdPackSnapshot | null = null;
  let firestoreError: LiveAdPackErrorInfo | undefined;
  let publisherAttached: boolean | undefined;

  // Polling is lazy — only activated when Firestore errors or times out.
  let unsubPolling: (() => void) | null = null;
  let gracePeriodTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  function startPolling() {
    if (unsubPolling || destroyed) return;
    unsubPolling = pollLiveAdPackState(
      jobId,
      (snap, attached) => {
        publisherAttached = attached;
        pollingSnap = snap;
        emit();
      },
      options.pollIntervalMs,
    );
  }

  function stopPolling() {
    if (unsubPolling) {
      try { unsubPolling(); } catch { /* ignore */ }
      unsubPolling = null;
    }
  }

  function emit() {
    const firestoreAds = firestoreSnap.adsOrdered.length;
    const pollingAds = pollingSnap?.adsOrdered.length ?? 0;
    const useFirestore =
      !!firestoreSnap.pack || (firestoreAds > 0 && firestoreAds >= pollingAds);
    let merged: LiveAdPackSnapshot;
    let source: CombinedSnapshot['source'];
    if (useFirestore) {
      merged = firestoreSnap;
      source = firestoreSnap.pack || firestoreAds > 0 ? 'firestore' : 'none';
    } else if (pollingSnap) {
      merged = pollingSnap;
      source = 'polling';
    } else {
      merged = firestoreSnap;
      source = 'none';
    }
    handler({ ...merged, source, firestoreError, publisherAttached });
  }

  const unsubFirestore = subscribeToLiveAdPack(
    jobId,
    (snap) => {
      firestoreSnap = snap;
      if (snap.pack || snap.adsOrdered.length > 0) {
        // Firestore is delivering data — cancel the grace-period timer and
        // stop polling if it somehow started.
        firestoreError = undefined;
        if (gracePeriodTimer) {
          clearTimeout(gracePeriodTimer);
          gracePeriodTimer = null;
        }
        stopPolling();
      }
      emit();
    },
    (errInfo) => {
      // Firestore errored — activate polling immediately.
      firestoreError = errInfo;
      if (gracePeriodTimer) {
        clearTimeout(gracePeriodTimer);
        gracePeriodTimer = null;
      }
      startPolling();
      emit();
    },
  );

  // Grace-period fallback: if Firestore hasn't delivered anything after
  // FIRESTORE_GRACE_PERIOD_MS, assume it's blocked and start polling.
  gracePeriodTimer = setTimeout(() => {
    gracePeriodTimer = null;
    if (!firestoreSnap.pack && firestoreSnap.adsOrdered.length === 0) {
      startPolling();
    }
  }, FIRESTORE_GRACE_PERIOD_MS);

  return () => {
    destroyed = true;
    if (gracePeriodTimer) clearTimeout(gracePeriodTimer);
    try { unsubFirestore(); } catch { /* ignore */ }
    stopPolling();
  };
}

/**
 * Hydration progress: returns 0..1 across the per-ad state machine.
 * Used by the UI to render aggregate progress bars without waiting on
 * the pipeline's coarser phase ticks.
 */
export function hydrationProgress(snapshot: LiveAdPackSnapshot): number {
  const pack = snapshot.pack;
  const expected = pack?.expected_ads ?? 0;
  if (!expected) return 0;

  const stateWeight: Record<LiveAdState, number> = {
    pending: 0,
    brief_ready: 0.2,
    image_preview: 0.5,
    image_ready: 0.7,
    reviewed: 0.85,
    final: 1.0,
    failed: 1.0, // terminal
  };

  let sum = 0;
  for (const ad of snapshot.adsOrdered) {
    sum += stateWeight[ad.state] ?? 0;
  }
  // Account for ads that haven't emitted yet — they sit at 0 implicitly.
  return Math.min(1, sum / expected);
}

/**
 * Convenience: how many ads have at least reached the given milestone?
 * Useful for the per-stage strip in the live view.
 */
export function adCountAtState(
  snapshot: LiveAdPackSnapshot,
  state: LiveAdState,
): number {
  const order: LiveAdState[] = [
    'pending',
    'brief_ready',
    'image_preview',
    'image_ready',
    'reviewed',
    'final',
  ];
  const targetRank = order.indexOf(state);
  if (targetRank < 0) return 0;
  return snapshot.adsOrdered.filter((ad) => {
    if (ad.state === 'failed') return false;
    return order.indexOf(ad.state) >= targetRank;
  }).length;
}
